// apps/web/src/layouts/components/sidebarBadgeTree.ts
//
// Núcleo puro da regra "o número aparece no ancestral colapsado mais alto":
// um item do menu admin nomeia sua contagem por `badgeKey`, e `sumBadges`
// agrega a contagem de um nó com a de todos os seus descendentes.
//
// Módulo sem React e sem @alamo/api de propósito — a soma é testável sozinha.

/** Nomeia a contagem que um item do menu admin exibe. */
export type SidebarBadgeKey =
  | 'approvals'
  | 'approvalQueue'
  | 'analystQueue'
  | 'commissionAttention'
  | 'competitorOfferQueue'
  | 'endorsementQueue'
  | 'paymentIncidentOpen'
  | 'reactivationBillingOpen'
  | 'quotationExceptionQueue'
  | 'support'
  | 'vehicleQueue';

/** Número corrente de cada badge. 0 significa "nada a exibir". */
export type SidebarBadgeCounts = Record<SidebarBadgeKey, number>;

export const EMPTY_SIDEBAR_BADGE_COUNTS: SidebarBadgeCounts = {
  approvals: 0,
  approvalQueue: 0,
  analystQueue: 0,
  commissionAttention: 0,
  competitorOfferQueue: 0,
  endorsementQueue: 0,
  paymentIncidentOpen: 0,
  reactivationBillingOpen: 0,
  quotationExceptionQueue: 0,
  support: 0,
  vehicleQueue: 0,
};

/** Forma mínima de um nó da árvore do menu para efeito de contagem. */
export interface BadgeCountableNode {
  badgeKey?: SidebarBadgeKey;
  children?: BadgeCountableNode[];
}

/**
 * Soma a contagem do nó com a de toda a sua descendência.
 *
 * O chamador passa a árvore já filtrada por permissão: contagem de item que o
 * usuário não enxerga não entra na soma do ancestral.
 */
export function sumBadges(node: BadgeCountableNode, counts: SidebarBadgeCounts): number {
  const own = node.badgeKey ? counts[node.badgeKey] : 0;
  return own + sumBadgeList(node.children ?? [], counts);
}

/** `sumBadges` aplicado a uma lista de irmãos — a raiz de um grupo é uma lista. */
export function sumBadgeList(nodes: BadgeCountableNode[], counts: SidebarBadgeCounts): number {
  return nodes.reduce((total, node) => total + sumBadges(node, counts), 0);
}
